import { useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router";

import { isProgramLead } from "@/components/helpers/workspace/permissions";
import { Box } from "@/components/layout/flexbox";
import { Nav } from "@/components/nav/Nav";
import { AddProgramComponentDialog } from "@/components/pages_logged_in/program/components/AddProgramComponentDialog";
import { CloseProgramComponentDialog } from "@/components/pages_logged_in/program/components/CloseProgramComponentDialog";
import {
  COMPONENT_DETAILS_PATHS,
  scrollWithOffset,
} from "@/components/pages_logged_in/program/components/component-utils";
import { DeleteProgramComponentDialog } from "@/components/pages_logged_in/program/components/DeleteProgramComponentDialog";
import { ProgramDetailsLoader } from "@/components/pages_logged_in/program/components/ProgramDetailsLoader";
import { ProgramComponentHeader } from "@/components/pages_logged_in/program/components/program-component/ProgramComponentHeader";
import { ComponentParticipants } from "@/components/pages_logged_in/program/components/program-details/ComponentParticipants";
import { ComponentSummary } from "@/components/pages_logged_in/program/components/program-details/ComponentSummary";
import { PerformancePerScenario } from "@/components/pages_logged_in/program/components/program-details/PerformancePerScenario";
import { SurveyQuestionAnalytics } from "@/components/pages_logged_in/program/components/program-details/SurveyQuestionAnalytics";
import { allowEditComponent } from "@/components/pages_logged_in/program/components/utils";
import { PermissionErrorPage } from "@/components/pages_public/403";
import { HEADER_HEIGHT } from "@/components/pieces/menu/MenuHeader";
import {
  ComponentStatus,
  ComponentTypeEnum,
  ProgramComponentForStatisticQueryDocument,
  ProgramComponentParticipantsQueryDocument,
  ProgramSurveyQuestionsAnalyticsQueryDocument,
  useProgramComponentForStatisticQuery,
  useProgramComponentQuery,
  useProgramQuery,
} from "@/graphql";
import {
  ComponentDialog,
  LeadComponent,
  MainComponent,
  ParticipantComponent,
} from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";

import { CoachesTable } from "./program-details/CoachesTable";
import { CreditUsage } from "./program-details/CreditUsage";
import { MeetingSessionAnalytics } from "./program-details/MeetingSessionAnalytics";
import { SessionFeedback } from "./program-details/SessionFeedback";

type ProgramComponent = LeadComponent | MainComponent | ParticipantComponent;

export function ProgramComponentDetails() {
  const { id, componentId } = useParams();
  const [selectedComponent, setSelectedComponent] =
    useState<ProgramComponent>();
  const [dialogOpen, setDialogOpen] = useState<ComponentDialog>();
  const [initialLoad, setInitialLoad] = useState(false);
  const isLead = isProgramLead(id);
  const navigate = useNavigate();
  const hashData = window.location.hash.slice(1);

  const { data: programData, loading: loadingProgram } = useProgramQuery({
    variables: { programId: id || "" },
    skip: !id,
  });

  const { data: programComponentData, loading: loadingComponent } =
    useProgramComponentQuery({
      variables: {
        programId: id || "",
        programComponentId: componentId || "",
      },
      skip: !id || !componentId,
      fetchPolicy: "cache-first",
    });

  const { data: programStatisticsData, loading: loadingProgramStatistics } =
    useProgramComponentForStatisticQuery({
      variables: { programComponentId: componentId || "" },
      skip: !componentId,
    });

  useEffect(() => {
    if (programComponentData?.programComponent && !initialLoad) {
      scrollWithOffset({
        componentTag: hashData,
        offset: -(HEADER_HEIGHT + 30),
      });
      setInitialLoad(true);
    }
  }, [programComponentData, hashData, initialLoad]);

  const programComponent = programComponentData?.programComponent;
  const surveyId = programComponent?.survey?.id;

  const component = programComponent ?? undefined; // Use undefined to avoid type errors

  const roleplayScenarios = ensureNonNullArray(component?.roleplayScenarios);
  const program = programData?.program;
  const status = component?.status || ComponentStatus.InDesign;
  const isProgramClosed = Boolean(program?.closedAt);
  const isRoleplay = component?.type === ComponentTypeEnum.AiRoleplay;
  const isEditable = allowEditComponent({
    component,
    isProgramClosed,
    isProgramLead: isLead,
  });

  const handleEditComponent = (component: ProgramComponent) => {
    setSelectedComponent(component);
    setDialogOpen("add-component");
  };

  const handleDeleteComponent = (component: ProgramComponent) => {
    setSelectedComponent(component);
    setDialogOpen("delete-component");
  };

  const handleCloseComponent = (component: ProgramComponent) => {
    setSelectedComponent(component);
    setDialogOpen("close-component");
  };

  const initPageSize = 5;
  const initStartPage = 1;

  if (
    (loadingProgramStatistics && !programStatisticsData) ||
    (loadingProgram && !programData) ||
    (loadingComponent && !programComponentData)
  ) {
    return (
      <ProgramDetailsLoader
        includeSidebar={true}
        hasHeader={false}
        sizeNavWidth={0}
      />
    );
  }

  if (
    (!isLead || !program || !programComponent) &&
    !loadingProgramStatistics &&
    !loadingProgram &&
    !loadingComponent
  ) {
    return <PermissionErrorPage />;
  }

  if (!component || !program) {
    return null;
  }

  return (
    <>
      <Nav />
      <Box className="xs:p-4 flex w-full flex-1 flex-col gap-6 md:p-6">
        <ProgramComponentHeader
          component={component}
          onEdit={handleEditComponent}
          onDelete={handleDeleteComponent}
          onClose={handleCloseComponent}
          isEditable={isEditable}
          view="details"
          showIcon={false}
          program={program}
        />

        {programStatisticsData?.programComponentForStatistic && program && (
          <ComponentSummary
            program={program}
            component={component}
            statisticComponent={
              programStatisticsData.programComponentForStatistic
            }
            isProgramLead={isLead}
            roleplayScenarios={roleplayScenarios}
          />
        )}

        {component && (
          <ComponentParticipants
            component={component}
            initStartPage={initStartPage}
            initPageSize={initPageSize}
            program={program}
          />
        )}

        {component?.type &&
          [ComponentTypeEnum.Survey, ComponentTypeEnum.ActionItem].includes(
            component?.type,
          ) && (
            <SurveyQuestionAnalytics
              surveyStatus={status}
              component={component}
            />
          )}

        {isRoleplay && componentId && (
          <PerformancePerScenario componentId={componentId} />
        )}
        {component?.type === ComponentTypeEnum.OneOneCoachingMaster && (
          <>
            <MeetingSessionAnalytics component={component} />
            <CoachesTable component={component} />
            <SessionFeedback component={component} />
            <CreditUsage component={component} />
          </>
        )}

        {selectedComponent && program && (
          <AddProgramComponentDialog
            componentId={selectedComponent?.id}
            extraOnSavedRefetchs={[
              {
                query: ProgramComponentForStatisticQueryDocument,
                variables: {
                  programComponentId: componentId,
                },
              },
              {
                query: ProgramComponentParticipantsQueryDocument,
                variables: {
                  programComponentId: componentId,
                  page: initStartPage,
                  size: initPageSize,
                },
              },
              ...(surveyId
                ? [
                    {
                      query: ProgramSurveyQuestionsAnalyticsQueryDocument,
                      variables: {
                        programSurveyId: surveyId,
                      },
                    },
                  ]
                : []),
            ]}
            programId={program.id}
            programStatus={program.status}
            targetLaunchDate={program.targetLaunchDate}
            open={
              dialogOpen === "add-component" || dialogOpen === "edit-component"
            }
            onClose={() => {
              setDialogOpen(undefined);
              setSelectedComponent(undefined);
            }}
          />
        )}

        {selectedComponent && (
          <>
            <DeleteProgramComponentDialog
              component={selectedComponent}
              programId={program.id}
              open={dialogOpen === "delete-component"}
              onClose={() => {
                setDialogOpen(undefined);
                setSelectedComponent(undefined);
              }}
              onDelete={() => {
                navigate(`/program/${id}/details`);
              }}
            />

            <CloseProgramComponentDialog
              component={selectedComponent}
              programId={program.id}
              open={dialogOpen === "close-component"}
              onClose={() => {
                setDialogOpen(undefined);
                setSelectedComponent(undefined);
              }}
              onCloseComponent={() => {
                if (selectedComponent?.id && program?.id) {
                  const path = component?.type
                    ? COMPONENT_DETAILS_PATHS[component?.type]
                    : "";
                  navigate(
                    `/programs/${program?.id}/${path}/${selectedComponent?.id}`,
                  );
                }
              }}
            />
          </>
        )}
      </Box>
    </>
  );
}
